import { makePreview, sanitizeClone } from "@shared/utils/json-sanitize";
import type { Snapshot, TelemetryEvent, RunStatus } from "../types";
import type { FrameLiveState, NodeErrorEntry, NodeOutputEntry } from "../types";

export const MAX_OUTPUT_JSON_CHARS = 10_000;

export interface RunEventContext {
  byFrame: Map<string, { nodeId: string; state: FrameLiveState; lastTs: number }>;
  countedFrames: Set<string>;
  completedFrames: Set<string>;
  failedFrames: Set<string>;
  runningCounts: Map<string, number>;
  waitingCounts: Map<string, number>;
  suspendedCounts: Map<string, number>;
}

export interface RunEventMeta {
  runId: string;
  flowId: string | null;
}

export interface FramesUpdate {
  running: Set<string>;
  waiting: Set<string>;
  suspended: Set<string>;
}

export interface RunEventReduction {
  status?: RunStatus | null;
  frames?: FramesUpdate;
  metrics?: Partial<{ queueLen: number; stackLen: number; suspendedLen: number }>;
  attempts?: Array<{ nodeId: string; delta: number }>;
  successes?: Array<{ nodeId: string; delta: number }>;
  failures?: Array<{ nodeId: string; delta: number }>;
  completedNodeIds?: string[];
  failedNodeIds?: string[];
  nodeError?: NodeErrorEntry | null;
  nodeOutput?: NodeOutputEntry | null;
}

export interface SnapshotReduction {
  snapshot: Snapshot;
  metrics: { queueLen: number; stackLen: number; suspendedLen: number };
  frames: FramesUpdate;
  paused: boolean | null;
  suspendedHeadNodeId: string | null;
  completedNodeIds?: string[];
  failedNodeIds?: string[];
  attempts?: Array<{ nodeId: string; delta: number }>;
  successes?: Array<{ nodeId: string; delta: number }>;
  failures?: Array<{ nodeId: string; delta: number }>;
  errors?: NodeErrorEntry[];
  outputs?: NodeOutputEntry[];
}

export const createRunEventContext = (): RunEventContext => ({
  byFrame: new Map(),
  countedFrames: new Set(),
  completedFrames: new Set(),
  failedFrames: new Set(),
  runningCounts: new Map(),
  waitingCounts: new Map(),
  suspendedCounts: new Map(),
});

export const isTerminalStatus = (status: RunStatus | null): boolean =>
  status === "completed" || status === "failed" || status === "canceled";

const asString = (value: unknown) => (typeof value === "string" ? value.toLowerCase() : undefined);

export function interpretStatusFromEvent(payload: TelemetryEvent | Record<string, unknown>): RunStatus | null {
  if (!payload || typeof payload !== "object") return null;
  const input: Record<string, unknown> = payload as Record<string, unknown>;
  const from = asString(input.status) ?? asString(input.state) ?? asString(input.type);
  if (from === "running") return "running";
  if (from === "paused") return "paused";
  return null;
}

const bump = (map: Map<string, number>, nodeId: string, delta: number) => {
  const next = (map.get(nodeId) ?? 0) + delta;
  if (next <= 0) map.delete(nodeId);
  else map.set(nodeId, next);
};

const recomputeFrames = (ctx: RunEventContext): FramesUpdate => ({
  running: new Set(ctx.runningCounts.keys()),
  waiting: new Set(ctx.waitingCounts.keys()),
  suspended: new Set(ctx.suspendedCounts.keys()),
});

const upsertFrame = (
  ctx: RunEventContext,
  frameId: string,
  nodeId: string,
  state: FrameLiveState,
  ts: number
): FramesUpdate => {
  const prev = ctx.byFrame.get(frameId);
  if (prev) {
    if (prev.state === "running") bump(ctx.runningCounts, prev.nodeId, -1);
    else if (prev.state === "waiting") bump(ctx.waitingCounts, prev.nodeId, -1);
    else if (prev.state === "suspended") bump(ctx.suspendedCounts, prev.nodeId, -1);
  }

  if (state === "completed" || state === "failed" || state === "cancelled") {
    ctx.byFrame.delete(frameId);
  } else {
    ctx.byFrame.set(frameId, { nodeId, state, lastTs: ts });
  }

  if (state === "running") bump(ctx.runningCounts, nodeId, +1);
  else if (state === "waiting") bump(ctx.waitingCounts, nodeId, +1);
  else if (state === "suspended") bump(ctx.suspendedCounts, nodeId, +1);

  return recomputeFrames(ctx);
};

export function extractNodeError(
  payload: Record<string, any>,
  nodeId: string,
  frameId: string,
  ts: number,
  meta: RunEventMeta
): NodeErrorEntry {
  const msg =
    (typeof payload.payload?.error === "string" && payload.payload.error) ||
    (typeof payload.payload?.error?.message === "string" && payload.payload.error.message) ||
    (typeof payload.error === "string" && payload.error) ||
    (typeof payload.error?.message === "string" && payload.error.message) ||
    (typeof payload.payload?.message === "string" && payload.payload.message) ||
    (typeof payload.message === "string" && payload.message) ||
    (typeof payload.errorMessage === "string" && payload.errorMessage) ||
    (typeof payload.reason === "string" && payload.reason) ||
    "Node error";

  const iteration =
    (typeof payload.iteration === "number" ? payload.iteration : undefined) ??
    (typeof payload.scope?.iteration === "number" ? payload.scope.iteration : undefined) ??
    (typeof payload.meta?.iteration === "number" ? payload.meta.iteration : undefined);

  const path: string[] | undefined = Array.isArray(payload.path)
    ? payload.path
    : Array.isArray(payload.scope?.path)
    ? payload.scope.path
    : undefined;

  const errorType: string | undefined =
    (typeof payload.error?.type === "string" && payload.error.type) ||
    (typeof payload.code === "string" && payload.code) ||
    (typeof payload.error?.code === "string" && payload.error.code) ||
    undefined;

  return {
    id: `${nodeId}:${frameId}:${ts}`,
    runId: meta.runId,
    flowId: meta.flowId,
    nodeId,
    frameId,
    ts,
    message: msg,
    iteration,
    path,
    errorType,
    cause: payload.error?.cause ?? payload.cause ?? undefined,
    details: payload,
  };
}

export function extractNodeOutput(
  payload: Record<string, any>,
  nodeId: string,
  frameId: string,
  ts: number,
  meta: RunEventMeta
): NodeOutputEntry | null {
  const raw =
    payload.payload?.outputs ??
    payload.payload?.result ??
    payload.outputs ??
    payload.result ??
    payload.payload?.data ??
    undefined;

  if (raw === undefined) return null;

  const iteration =
    (typeof payload.iteration === "number" ? payload.iteration : undefined) ??
    (typeof payload.scope?.iteration === "number" ? payload.scope.iteration : undefined) ??
    (typeof payload.meta?.iteration === "number" ? payload.meta.iteration : undefined);

  try {
    const { clone, size } = sanitizeClone(raw, { maxChars: MAX_OUTPUT_JSON_CHARS });
    return {
      id: `${nodeId}:${frameId}:${ts}`,
      runId: meta.runId,
      flowId: meta.flowId,
      nodeId,
      frameId,
      ts,
      iteration,
      outputs: clone,
      preview: makePreview(clone),
      size,
    };
  } catch {
    return {
      id: `${nodeId}:${frameId}:${ts}`,
      runId: meta.runId,
      flowId: meta.flowId,
      nodeId,
      frameId,
      ts,
      iteration,
      outputs: { __error__: "Failed to sanitize output", raw: makePreview(raw) },
      preview: makePreview(raw),
    };
  }
}

const normalizeType = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  if (raw === "enter") return "start";
  if (raw === "exit") return "complete";
  return raw;
};

export function reduceRunEvent(
  payload: TelemetryEvent | Record<string, unknown>,
  ctx: RunEventContext,
  meta: RunEventMeta
): RunEventReduction | null {
  if (!payload || typeof payload !== "object") return null;
  const data = payload as Record<string, any>;
  const nodeId = data.nodeId ?? data.scope?.nodeId;
  const frameId = data.frameId ?? data.scope?.frameId;

  const reduction: RunEventReduction = {};
  const status = interpretStatusFromEvent(data);
  const noNodeInfo = !nodeId && !frameId && !(data.scope && (data.scope.nodeId || data.scope.frameId));
  if (status && noNodeInfo) {
    reduction.status = status;
  }

  if (typeof data.action === "string" && typeof data.scope?.len === "number") {
    const metrics: RunEventReduction["metrics"] = {};
    if (data.action === "queue") metrics.queueLen = data.scope.len;
    if (data.action === "stack") metrics.stackLen = data.scope.len;
    if (data.action === "suspended") metrics.suspendedLen = data.scope.len;
    if (Object.keys(metrics).length) {
      reduction.metrics = { ...(reduction.metrics ?? {}), ...metrics };
    }
  }

  if (!nodeId || !frameId) {
    return Object.keys(reduction).length ? reduction : null;
  }

  const ts = typeof data.ts === "number" ? data.ts : Date.now();
  const type = normalizeType(data.type ?? data.event ?? data.state);
  if (!type) {
    return Object.keys(reduction).length ? reduction : null;
  }

  const nodeKey = String(nodeId);
  const frameKey = String(frameId);

  switch (type) {
    case "queue": {
      reduction.frames = upsertFrame(ctx, frameKey, nodeKey, "queued", ts);
      break;
    }
    case "start": {
      reduction.frames = upsertFrame(ctx, frameKey, nodeKey, "running", ts);
      if (!ctx.countedFrames.has(frameKey)) {
        ctx.countedFrames.add(frameKey);
        reduction.attempts = [...(reduction.attempts ?? []), { nodeId: nodeKey, delta: 1 }];
      }
      break;
    }
    case "wait": {
      reduction.frames = upsertFrame(ctx, frameKey, nodeKey, "waiting", ts);
      break;
    }
    case "suspend": {
      reduction.frames = upsertFrame(ctx, frameKey, nodeKey, "suspended", ts);
      break;
    }
    case "resume": {
      reduction.frames = upsertFrame(ctx, frameKey, nodeKey, "running", ts);
      break;
    }
    case "complete": {
      reduction.frames = upsertFrame(ctx, frameKey, nodeKey, "completed", ts);
      reduction.completedNodeIds = [...(reduction.completedNodeIds ?? []), nodeKey];
      if (!ctx.completedFrames.has(frameKey)) {
        ctx.completedFrames.add(frameKey);
        reduction.successes = [...(reduction.successes ?? []), { nodeId: nodeKey, delta: 1 }];
      }
      reduction.nodeOutput = extractNodeOutput(data, nodeKey, frameKey, ts, meta);
      break;
    }
    case "error": {
      reduction.frames = upsertFrame(ctx, frameKey, nodeKey, "failed", ts);
      reduction.failedNodeIds = [...(reduction.failedNodeIds ?? []), nodeKey];
      if (!ctx.countedFrames.has(frameKey)) {
        ctx.countedFrames.add(frameKey);
        reduction.attempts = [...(reduction.attempts ?? []), { nodeId: nodeKey, delta: 1 }];
      }
      if (!ctx.failedFrames.has(frameKey)) {
        ctx.failedFrames.add(frameKey);
        reduction.failures = [...(reduction.failures ?? []), { nodeId: nodeKey, delta: 1 }];
      }
      reduction.nodeError = extractNodeError(data, nodeKey, frameKey, ts, meta);
      break;
    }
    case "cancel": {
      reduction.frames = upsertFrame(ctx, frameKey, nodeKey, "cancelled", ts);
      break;
    }
  }

  return Object.keys(reduction).length ? reduction : null;
}

const normalizeStatusString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  return value.toLowerCase();
};

const pickNodeId = (entries: any[]): string | null => {
  for (const item of entries) {
    const nid = item?.nodeId ?? item?.node?.id;
    if (nid) return String(nid);
  }
  return null;
};

const normalizeSnapshotType = (value: unknown): string => {
  const normalized = String(value ?? "").toLowerCase();
  if (normalized === "enter") return "start";
  if (normalized === "exit") return "complete";
  return normalized;
};

export function reduceSnapshot(snapshot: Snapshot, ctx: RunEventContext, meta: RunEventMeta): SnapshotReduction {
  const snap: any = snapshot;
  const queues: any = snap?.queues ?? {};
  const queueArr: any[] = Array.isArray(queues?.queue) ? queues.queue : [];
  const stackArr: any[] = Array.isArray(queues?.stack) ? queues.stack : [];
  const suspendedArr: any[] = Array.isArray(snap?.suspended) ? snap.suspended : [];

  const runId =
    meta.runId ||
    (snap?.runId ? String(snap.runId) : snap?.run?.id ? String(snap.run.id) : snap?.id ? String(snap.id) : "");
  const flowId =
    meta.flowId ??
    (snap?.flowId
      ? String(snap.flowId)
      : snap?.run?.flowId
      ? String(snap.run.flowId)
      : snap?.flow?.id
      ? String(snap.flow.id)
      : null);
  const effectiveMeta: RunEventMeta = {
    runId,
    flowId: flowId ?? null,
  };

  // Preserve running/waiting frames from existing context
  const preserved = new Map<string, { nodeId: string; state: FrameLiveState; lastTs: number }>();
  for (const [frameId, entry] of ctx.byFrame.entries()) {
    if (entry.state === "running" || entry.state === "waiting") {
      preserved.set(frameId, entry);
    }
  }
  ctx.byFrame.clear();
  for (const [frameId, entry] of preserved.entries()) {
    ctx.byFrame.set(frameId, entry);
  }

  const now = Date.now();
  const assignQueue = (arr: any[]) => {
    for (const fr of arr) {
      const fidRaw = fr?.id ?? fr?.frameId;
      const nidRaw = fr?.nodeId ?? fr?.node?.id;
      if (!fidRaw || !nidRaw) continue;
      const fid = String(fidRaw);
      const nid = String(nidRaw);
      const existing = ctx.byFrame.get(fid);
      if (!existing || existing.state === "queued") {
        ctx.byFrame.set(fid, { nodeId: nid, state: "queued", lastTs: now });
      }
    }
  };

  assignQueue(queueArr);
  assignQueue(stackArr);

  ctx.suspendedCounts.clear();
  for (const fr of suspendedArr) {
    const fidRaw = fr?.id ?? fr?.frameId;
    const nidRaw = fr?.nodeId ?? fr?.node?.id;
    if (!fidRaw || !nidRaw) continue;
    const fid = String(fidRaw);
    const nid = String(nidRaw);
    ctx.byFrame.set(fid, { nodeId: nid, state: "suspended", lastTs: now });
    bump(ctx.suspendedCounts, nid, +1);
  }

  const attempts = new Map<string, number>();
  const successes = new Map<string, number>();
  const failures = new Map<string, number>();
  const completedNodeIds = new Set<string>();
  const failedNodeIds = new Set<string>();
  const collectedErrors: NodeErrorEntry[] = [];
  const collectedOutputs: NodeOutputEntry[] = [];

  const history: any[] = Array.isArray(snap?.history) ? snap.history : [];
  for (const rawEvent of history) {
    const nodeIdRaw = rawEvent?.nodeId ?? rawEvent?.node?.id ?? rawEvent?.scope?.nodeId;
    if (!nodeIdRaw) continue;
    const nodeId = String(nodeIdRaw);
    const frameIdRaw = rawEvent?.frameId ?? rawEvent?.frame?.id ?? rawEvent?.id;
    const frameId = frameIdRaw ? String(frameIdRaw) : "";
    const type = normalizeSnapshotType(rawEvent?.type ?? rawEvent?.event ?? rawEvent?.state);
    const ts = typeof rawEvent?.ts === "number" ? rawEvent.ts : now;

    if (type === "complete") {
      completedNodeIds.add(nodeId);
      if (frameId && !ctx.completedFrames.has(frameId)) {
        ctx.completedFrames.add(frameId);
        successes.set(nodeId, (successes.get(nodeId) ?? 0) + 1);
      }
      if (frameId) {
        const output = extractNodeOutput(rawEvent, nodeId, frameId, ts, effectiveMeta);
        if (output) {
          collectedOutputs.push(output);
        }
      }
    } else if (type === "error") {
      failedNodeIds.add(nodeId);
      if (frameId && !ctx.failedFrames.has(frameId)) {
        ctx.failedFrames.add(frameId);
        failures.set(nodeId, (failures.get(nodeId) ?? 0) + 1);
      }
      if (frameId) {
        const errorEntry = extractNodeError(rawEvent, nodeId, frameId, ts, effectiveMeta);
        collectedErrors.push(errorEntry);
      }
    }

    if (frameId && !frameId.startsWith("group:")) {
      if (!ctx.countedFrames.has(frameId)) {
        ctx.countedFrames.add(frameId);
        if (type === "start" || type === "complete" || type === "error") {
          attempts.set(nodeId, (attempts.get(nodeId) ?? 0) + 1);
        }
      }
    }
  }

  for (const [frameId, frame] of ctx.byFrame.entries()) {
    if ((frame.state === "running" || frame.state === "waiting" || frame.state === "suspended") && !ctx.countedFrames.has(frameId)) {
      ctx.countedFrames.add(frameId);
      attempts.set(frame.nodeId, (attempts.get(frame.nodeId) ?? 0) + 1);
    }
  }

  ctx.runningCounts.clear();
  ctx.waitingCounts.clear();
  ctx.suspendedCounts.clear();
  for (const entry of ctx.byFrame.values()) {
    if (entry.state === "running") bump(ctx.runningCounts, entry.nodeId, +1);
    else if (entry.state === "waiting") bump(ctx.waitingCounts, entry.nodeId, +1);
    else if (entry.state === "suspended") bump(ctx.suspendedCounts, entry.nodeId, +1);
  }

  const paused =
    typeof snap?.paused === "boolean"
      ? snap.paused
      : (() => {
          const statusStr =
            normalizeStatusString(snap?.status) ??
            normalizeStatusString(snap?.state) ??
            normalizeStatusString(snap?.run?.status) ??
            normalizeStatusString(snap?.meta?.state);
          if (statusStr === "paused") return true;
          if (statusStr === "running") return false;
          return null;
        })();

  return {
    snapshot,
    metrics: {
      queueLen: queueArr.length || 0,
      stackLen: stackArr.length || 0,
      suspendedLen: suspendedArr.length || 0,
    },
    frames: recomputeFrames(ctx),
    paused,
    suspendedHeadNodeId: paused ? pickNodeId(suspendedArr) ?? pickNodeId(queueArr) ?? pickNodeId(stackArr) : null,
    completedNodeIds: completedNodeIds.size ? Array.from(completedNodeIds) : undefined,
    failedNodeIds: failedNodeIds.size ? Array.from(failedNodeIds) : undefined,
    attempts: attempts.size
      ? Array.from(attempts.entries()).map(([nodeId, delta]) => ({ nodeId, delta }))
      : undefined,
    successes: successes.size
      ? Array.from(successes.entries()).map(([nodeId, delta]) => ({ nodeId, delta }))
      : undefined,
    failures: failures.size
      ? Array.from(failures.entries()).map(([nodeId, delta]) => ({ nodeId, delta }))
      : undefined,
    errors: collectedErrors.length ? collectedErrors : undefined,
    outputs: collectedOutputs.length ? collectedOutputs : undefined,
  };
}
